import { getAnalytics, logEvent, setUserId, setUserProperties, isSupported } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import { app } from './firebase';
import { Capacitor } from '@capacitor/core';
import type { EntitlementId } from '../types/subscriptions';

// Lazy-loaded Fullstory references
let fsApi: typeof import('@fullstory/browser').FullStory | null = null;

// Lazy-loaded PostHog instance (null until init, or when no key is configured)
let phApi: typeof import('posthog-js').default | null = null;
let phEnvironment: string | null = null;

/**
 * The initialized PostHog instance, or null when PostHog is not configured
 * (no key, tests, previews) or has not finished init. Exposed only for the
 * Sentry replay link in `apps/enough/src/lib/sentry.ts` — every other caller
 * goes through trackEvent / identifyUser and friends.
 */
export const getPostHog = (): typeof import('posthog-js').default | null => phApi;

/** Drop the query string and fragment from a URL-ish string. */
const stripUrlQuery = (value: string): string => value.split(/[?#]/)[0];

/** URL-valued property keys whose query/fragment can carry auth codes
 *  (Firebase `oobCode`, Plaid `oauth_state_id`, Stripe `session_id`, …). */
const URL_PROPERTY_KEYS = new Set([
  '$current_url',
  '$referrer',
  '$pathname',
  '$initial_current_url',
  '$initial_referrer',
  '$initial_pathname',
  '$session_entry_url',
  '$session_entry_referrer',
  '$session_entry_pathname',
  '$prev_pageview_pathname',
]);
const isUrlPropertyKey = (key: string): boolean =>
  URL_PROPERTY_KEYS.has(key) || key.endsWith('_url') || key.endsWith('_referrer');

const looksLikeUrlWithQuery = (value: string): boolean =>
  /[?#]/.test(value) && (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith('/'));

const CLICK_EVENTS = new Set(['$autocapture', '$rageclick', '$dead_click']);
const PRIVATE_CLASS_RE = /(^|[^\w-])(ph-mask|ph-no-capture)(?![\w-])/;

/** Clean one autocapture element attribute; undefined means drop it. */
const cleanElementAttr = (key: string, value: unknown): unknown => {
  if (key === 'attr__value') return undefined; // input values never leave the device
  if (typeof value !== 'string') return value;
  if (key === 'href' || key === 'attr__href') return stripUrlQuery(value);
  if (key.startsWith('attr__') && looksLikeUrlWithQuery(value)) return stripUrlQuery(value);
  return value;
};

const cleanUrlProps = (props: Record<string, unknown> | undefined): void => {
  if (!props || typeof props !== 'object') return;
  for (const key of Object.keys(props)) {
    const value = props[key];
    if (typeof value === 'string' && isUrlPropertyKey(key)) {
      props[key] = stripUrlQuery(value);
    }
  }
};

/** Replay snapshots carry rrweb records; the Meta record (and any plugin
 *  record) names the page as `data.href` / `data.url` with its full query, so a
 *  reset link's `oobCode` or a Plaid `oauth_state_id` would be visible to a
 *  replay viewer. Strip in place; never throw. */
const cleanSnapshotUrls = (snapshot: unknown): void => {
  const records = Array.isArray(snapshot) ? snapshot : [snapshot];
  for (const rec of records) {
    if (!rec || typeof rec !== 'object') continue;
    const data = (rec as { data?: unknown }).data;
    if (!data || typeof data !== 'object') continue;
    const d = data as Record<string, unknown>;
    for (const key of ['href', 'url']) {
      if (typeof d[key] === 'string') d[key] = stripUrlQuery(d[key] as string);
    }
    const payload = d.payload;
    if (payload && typeof payload === 'object') {
      const p = payload as Record<string, unknown>;
      for (const key of ['href', 'url']) {
        if (typeof p[key] === 'string') p[key] = stripUrlQuery(p[key] as string);
      }
    }
  }
};

/**
 * PostHog `before_send`: strip query strings and fragments from every URL
 * property and autocaptured href, and drop `attr__value` (plus any attribute
 * that is a URL with a query) from `$elements` / `$elements_chain`, and strip
 * the page URL inside `$snapshot` replay records (rrweb Meta `data.href`). Element
 * attributes are otherwise kept (`mask_all_element_attributes: false`) so
 * clicks stay attributable by id / class / data-testid. Never throws; on any
 * error the event is returned as-is rather than dropped.
 */
export const sanitizePostHogEvent = <T extends { properties?: Record<string, unknown>; $set?: Record<string, unknown>; $set_once?: Record<string, unknown> } | null>(
  event: T,
): T => {
  if (!event) return event;
  try {
    cleanUrlProps(event.properties);
    cleanUrlProps(event.$set);
    cleanUrlProps(event.$set_once);
    cleanUrlProps(event.properties?.$set as Record<string, unknown> | undefined);
    cleanUrlProps(event.properties?.$set_once as Record<string, unknown> | undefined);
    const props = event.properties;
    // A click inside a user-data region (.ph-mask / .ph-no-capture) must not
    // ship that region's text (merchant, amount) as autocaptured element text.
    const eventName = (event as { event?: unknown }).event;
    if (props && typeof eventName === 'string' && CLICK_EVENTS.has(eventName)) {
      const chain = typeof props.$elements_chain === 'string' ? props.$elements_chain : '';
      const elements = Array.isArray(props.$elements) ? (props.$elements as Array<Record<string, unknown>>) : [];
      const inMaskedRegion =
        PRIVATE_CLASS_RE.test(chain) ||
        elements.some((el) => el && typeof el.attr__class === 'string' && PRIVATE_CLASS_RE.test(el.attr__class))
        || elements.some((el) => el && Array.isArray(el.attr_class) && (el.attr_class as unknown[]).some((c) => typeof c === 'string' && PRIVATE_CLASS_RE.test(c)));
      if (inMaskedRegion) {
        delete props.$el_text;
        elements.forEach((el) => {
          if (el && typeof el === 'object') {
            delete el.$el_text;
            delete el.text;
          }
        });
        if (chain) props.$elements_chain = chain.replace(/(^|[^\w$-])text="(?:[^"\\]|\\.)*"/g, '$1');
      }
    }
    if (props && eventName === '$snapshot' && props.$snapshot_data !== undefined) {
      cleanSnapshotUrls(props.$snapshot_data);
    }
    if (props && Array.isArray(props.$elements)) {
      props.$elements = (props.$elements as Array<Record<string, unknown>>).map((el) => {
        if (!el || typeof el !== 'object') return el;
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(el)) {
          const cleaned = cleanElementAttr(k, v);
          if (cleaned !== undefined) out[k] = cleaned;
        }
        return out;
      });
    }
    if (props && typeof props.$elements_chain === 'string') {
      props.$elements_chain = props.$elements_chain.replace(
        /([\w$-]+)="((?:[^"\\]|\\.)*)"/g,
        (match: string, key: string, value: string) => {
          const cleaned = cleanElementAttr(key, value);
          if (cleaned === undefined) return '';
          return cleaned === value ? match : `${key}="${cleaned as string}"`;
        },
      );
    }
  } catch {
    // never block an event on sanitizer failure
  }
  return event;
};

// Lazy-loaded Capacitor Firebase Analytics (native only)
let capAnalytics: typeof import('@capacitor-firebase/analytics').FirebaseAnalytics | null = null;

let analytics: Analytics | null = null;
let initialized = false;

// Promise that resolves when init completes — tracking functions wait on this
let initPromise: Promise<void> | null = null;

// Queue of operations to replay after init. null = init done, run immediately.
let pendingOps: Array<() => void> | null = [];

export interface AnalyticsUserProperties {
  /** Derived from the entitlements SoT — spelled as EntitlementId rather than
   *  a second hand-maintained union, so a new product line can't be added to
   *  the SoT and silently stay untrackable here. */
  subscription_tier: EntitlementId | 'free';
  has_connected_accounts: boolean;
  platform: 'web' | 'ios' | 'android';
  app_version: string;
  email: string;
}

// Resolver for the in-flight idle deferral (null once init has passed it).
let endIdleDeferral: (() => void) | null = null;

/**
 * Resolve once the browser is idle (or a timeout elapses) so the heavy analytics
 * SDK downloads (Fullstory's fs.js, Firebase Analytics) don't compete with first
 * paint. Falls back to a short timeout where requestIdleCallback is unavailable
 * (older WebViews, non-browser/test environments).
 */
const whenIdle = (): Promise<void> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      endIdleDeferral = null;
      resolve();
    };
    // Exposed so a conversion-critical event or a backgrounding app can end the
    // deferral early — see flushAnalytics().
    endIdleDeferral = finish;
    const ric =
      typeof window !== 'undefined'
        ? (window as unknown as {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
          }).requestIdleCallback
        : undefined;
    if (ric) {
      ric(finish, { timeout: 3000 });
    } else {
      setTimeout(finish, 1500);
    }
  });

/**
 * Stop deferring SDK init and hand every queued event to the transports now.
 *
 * Why: events fired before init completes sit in `pendingOps` — plain JS
 * memory. On native that queue dies with the WebView if the OS suspends or
 * kills the app first, and the native Firebase SDK (which persists events to
 * disk the moment `logEvent` reaches it) never sees them. Conversion events
 * call this so a signup is never parked behind the idle deferral, and the
 * app's own background transition calls it so nothing is left queued when
 * the WebView goes away. Resolves when init has finished (or immediately if
 * initAnalytics was never called).
 */
export const flushAnalytics = (): Promise<void> => {
  endIdleDeferral?.();
  return initPromise ?? Promise.resolve();
};

/**
 * Hand queued events to the native SDK before the app is backgrounded. The
 * native Firebase SDK uploads its persisted batch on background itself; the
 * only part JS controls is making sure the events reached it.
 */
const armBackgroundFlush = (): void => {
  if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushAnalytics();
  });
};

/**
 * Initialize GA4 (Firebase Analytics), Fullstory and PostHog.
 * Safe to call multiple times — only the first call takes effect.
 * Fullstory and PostHog are optional: if a config value is missing, that SDK is skipped.
 *
 * The SDK loads are deferred until the browser is idle so they don't compete with
 * first paint. The replay queue is armed synchronously (initPromise/pendingOps are
 * set before this returns), so any events fired during the deferral are queued and
 * replayed once init completes — nothing is lost.
 */
export const initAnalytics = (config: {
  fullstoryOrgId?: string;
  fullstoryAssetMapId?: string;
  posthogKey?: string;
  posthogHost?: string;
  /** Registered as the `app_version` super property so pre-login events carry it. */
  appVersion?: string;
  /** Build environment ('development' | 'production'); dev and prod share one
   *  PostHog project, so every event and person carries it. */
  environment?: string;
}): void => {
  if (initialized) return;
  initialized = true;
  armBackgroundFlush();

  initPromise = (async () => {
    // Wait for the app to become interactive before pulling in the heavy SDKs.
    await whenIdle();

    // On native, use the Capacitor plugin (OS-level Firebase SDK) instead of the
    // JS SDK to avoid sending duplicate events to the same GA4 property.
    if (Capacitor.isNativePlatform()) {
      try {
        const cap = await import('@capacitor-firebase/analytics');
        capAnalytics = cap.FirebaseAnalytics;
        await capAnalytics.setEnabled({ enabled: true });
      } catch (e) {
        console.warn('[Analytics] Capacitor Firebase Analytics init failed:', e);
      }
    } else {
      // Web — use the Firebase JS SDK
      try {
        const supported = await isSupported();
        if (supported) {
          analytics = getAnalytics(app);
        }
      } catch (e) {
        console.warn('[Analytics] Firebase Analytics not supported:', e);
      }
    }

    // Fullstory
    if (config.fullstoryOrgId) {
      try {
        const fs = await import('@fullstory/browser');
        fsApi = fs.FullStory;
        const initOpts: import('@fullstory/browser').SnippetOptions = {
          orgId: config.fullstoryOrgId,
          devMode: false,
        };
        if (config.fullstoryAssetMapId) {
          initOpts.assetMapId = config.fullstoryAssetMapId;
        }
        fs.init(initOpts);
      } catch (e) {
        console.warn('[Analytics] Fullstory init failed:', e);
      }
    }

    // PostHog — session replay, rage/dead clicks, product-event mirror.
    // Privacy contract: docs/roadmap/2026/Q4/02-PostHog-Migration/POSTHOG_MIGRATION_PLAN.md §2.
    if (config.posthogKey) {
      try {
        const { default: posthog } = await import('posthog-js');
        // Super properties that tell web / iOS / Android apart. Both native
        // apps serve from https://localhost, so $host / $current_url are the
        // same on iOS and Android and the UA-derived $os is approximate —
        // filter PostHog on `platform`, never on $host or $os.
        const superProps = {
          platform: Capacitor.getPlatform(),
          is_native: Capacitor.isNativePlatform(),
          app_version: config.appVersion ?? '',
          environment: config.environment ?? 'development',
        };
        phEnvironment = superProps.environment;
        posthog.init(config.posthogKey, {
          api_host: config.posthogHost || 'https://us.i.posthog.com',
          persistence: 'localStorage+cookie',
          capture_pageview: false,
          capture_pageleave: true,
          autocapture: { element_allowlist: ['button', 'a', 'input', 'select', 'textarea'] },
          // Autocapture keeps button labels (our copy); user-data regions opt
          // out with .ph-no-capture / .ph-mask in the components.
          mask_all_text: false,
          mask_all_element_attributes: false,
          rageclick: true,
          capture_dead_clicks: true,
          // network_timing off: replay never records request URLs.
          capture_performance: { web_vitals: true, network_timing: false },
          before_send: sanitizePostHogEvent,
          session_recording: {
            // No network capture in replay (also off in the project console).
            recordHeaders: false,
            recordBody: false,
            // Selective masking: replays stay readable; only the user's own
            // data is hidden. Form fields are always masked; `.ph-mask`
            // replaces an element's text with *** (layout kept); `.ph-no-capture`
            // blocks a whole region (Plaid / Spinwheel / Stripe hosts).
            maskAllInputs: true,
            maskTextSelector: '.ph-mask',
            blockSelector: '.ph-no-capture',
            recordCrossOriginIframes: false,
            // Native builds serve from https://localhost, which the replay
            // player cannot fetch: inline stylesheets so iOS / Android replays
            // render (Phase 2b). If fonts are missing, try collectFonts next.
            inlineStylesheet: true,
          },
          disable_surveys: true,
          person_profiles: 'identified_only',
          loaded: (ph) => {
            ph.register(superProps);
          },
        });
        // Register synchronously too: `loaded` can fire after the first
        // capture, and the queued pendingOps replay right after this block.
        posthog.register(superProps);
        phApi = posthog;
        if (superProps.is_native) {
          // Native build number; fire-and-forget, never blocks init.
          // (@capacitor/device is not a dependency, so no device model / OS version.)
          import('@capacitor/app')
            .then(({ App }) => App.getInfo())
            .then((info) => {
              if (info?.build) posthog.register({ app_build: String(info.build) });
            })
            .catch(() => {});
        }
      } catch (e) {
        console.warn('[Analytics] PostHog init failed:', e);
      }
    }

    // Replay any events that were queued during init
    const ops = pendingOps ?? [];
    pendingOps = null; // signal: init done, run immediately from now on
    ops.forEach(op => op());
  })();
};

/** Run an operation now if init is done, otherwise queue it for replay. */
const whenReady = (op: () => void): void => {
  if (!initPromise) return; // initAnalytics was never called
  if (pendingOps === null) {
    op(); // init done — run immediately
  } else {
    pendingOps.push(op); // still initializing — queue for replay
  }
};

/**
 * Identify the current user for GA4, Fullstory and PostHog (PostHog never gets email).
 * Call on auth state change and when user properties change (e.g. subscription).
 *
 * Only the properties actually passed are set — omitted keys keep their
 * previously reported value, so callers that know one slice of state (auth,
 * subscription, accounts) don't reset each other's properties.
 */
export const identifyUser = (uid: string, properties?: Partial<AnalyticsUserProperties>): void => {
  const doIdentify = () => {
    const stringProps: Record<string, string> = {};
    if (properties) {
      if (properties.subscription_tier !== undefined) stringProps.subscription_tier = properties.subscription_tier;
      if (properties.has_connected_accounts !== undefined) stringProps.has_connected_accounts = String(properties.has_connected_accounts);
      if (properties.platform !== undefined) stringProps.platform = properties.platform;
      if (properties.app_version !== undefined) stringProps.app_version = properties.app_version;
    }
    if (analytics) {
      setUserId(analytics, uid);
      if (Object.keys(stringProps).length > 0) {
        setUserProperties(analytics, stringProps);
      }
    }
    if (capAnalytics) {
      capAnalytics.setUserId({ userId: uid }).catch(() => {});
      Object.entries(stringProps).forEach(([key, value]) => {
        capAnalytics!.setUserProperty({ key, value }).catch(() => {});
      });
    }
    if (fsApi) {
      fsApi.identify(uid, properties ? {
        ...(properties.email ? { email: properties.email } : {}),
        ...(properties.subscription_tier !== undefined ? { subscription_tier_str: properties.subscription_tier } : {}),
        ...(properties.has_connected_accounts !== undefined ? { has_connected_accounts_bool: properties.has_connected_accounts } : {}),
        ...(properties.platform !== undefined ? { platform_str: properties.platform } : {}),
        ...(properties.app_version !== undefined ? { app_version_str: properties.app_version } : {}),
      } : {});
    }
    if (phApi) {
      // Firebase uid as distinct id; never email or any other PII.
      const phProps: Record<string, string | boolean> = {};
      if (properties) {
        if (properties.subscription_tier !== undefined) phProps.subscription_tier = properties.subscription_tier;
        if (properties.has_connected_accounts !== undefined) phProps.has_connected_accounts = properties.has_connected_accounts;
        if (properties.platform !== undefined) phProps.platform = properties.platform;
        if (properties.app_version !== undefined) phProps.app_version = properties.app_version;
      }
      phProps.is_native = Capacitor.isNativePlatform();
      if (phEnvironment) phProps.environment = phEnvironment;
      // Second argument is $set: person properties for the identified user.
      phApi.identify(uid, phProps);
    }
  };
  whenReady(doIdentify);
};

/**
 * Report whether the user has any connected financial accounts.
 * Separate from identifyUser because the callers that identify (auth,
 * subscription) don't observe account state — this is driven by the
 * accounts data layer whenever connection state changes.
 */
export const setConnectedAccountsProperty = (hasConnectedAccounts: boolean): void => {
  const doSet = () => {
    const value = String(hasConnectedAccounts);
    if (analytics) {
      setUserProperties(analytics, { has_connected_accounts: value });
    }
    if (capAnalytics) {
      capAnalytics.setUserProperty({ key: 'has_connected_accounts', value }).catch(() => {});
    }
    if (fsApi) {
      fsApi.setUserVars({ has_connected_accounts_bool: hasConnectedAccounts });
    }
    if (phApi) {
      phApi.setPersonProperties({ has_connected_accounts: hasConnectedAccounts });
    }
  };
  whenReady(doSet);
};

/**
 * Set one string-valued user property on every transport. For the slices of
 * state that are neither auth nor subscription: the experiment arm
 * (`exp_<slot>`, so GA4 funnels can be split by arm) and `internal_user_flag`
 * (the prod Remote Config "Internal Users" condition and the GA4 exclusion
 * segment both key on it). GA4 caps names at 24 characters and values at 36.
 */
export const setAnalyticsUserProperty = (key: string, value: string): void => {
  const doSet = () => {
    if (analytics) {
      setUserProperties(analytics, { [key]: value });
    }
    if (capAnalytics) {
      capAnalytics.setUserProperty({ key, value }).catch(() => {});
    }
    if (fsApi) {
      fsApi.setUserVars({ [`${key}_str`]: value });
    }
    if (phApi) {
      phApi.setPersonProperties({ [key]: value });
    }
  };
  whenReady(doSet);
};

/**
 * Clear user identity on logout.
 */
export const clearUser = (): void => {
  const doClear = () => {
    if (analytics) {
      setUserId(analytics, null as unknown as string);
    }
    if (capAnalytics) {
      capAnalytics.setUserId({ userId: null as unknown as string }).catch(() => {});
    }
    if (fsApi) {
      fsApi.anonymize();
    }
    if (phApi) {
      phApi.reset();
    }
  };
  whenReady(doClear);
};

type EventParams = Record<string, string | number | boolean>;

/** Send one event to every initialized transport. Resolves once the native
 *  bridge call (the only asynchronous transport) has been accepted or has
 *  failed — the web SDK and Fullstory enqueue synchronously. */
const sendEvent = (name: string, params?: EventParams): Promise<void> => {
  // Never throws: a transport failure must not surface as an app error
  // (trackCriticalEvent is awaited inside the signup path's try block).
  try {
    if (analytics) {
      logEvent(analytics, name, params);
    }
    if (fsApi) {
      fsApi.event(name, params ?? {});
    }
    if (phApi) {
      phApi.capture(name, params ?? {});
    }
    if (capAnalytics) {
      return capAnalytics.logEvent({ name, params: params ?? {} }).catch(() => {});
    }
  } catch {
    // fall through — the caller has nothing to do about a transport error
  }
  return Promise.resolve();
};

/**
 * Track a custom event to GA4, Fullstory and PostHog.
 * Params are forwarded as-is to every platform.
 */
export const trackEvent = (name: string, params?: EventParams): void => {
  whenReady(() => {
    void sendEvent(name, params);
  });
};

/**
 * Track a conversion-critical event and resolve once the transports have it.
 *
 * Differences from trackEvent, both aimed at the WebView-lifecycle loss case
 * (a slow signup that completes as the user backgrounds or closes the app):
 *  - it ends the idle deferral, so the event is never parked in JS memory
 *    waiting for init while the WebView can be torn down;
 *  - it resolves only after the native bridge has accepted the event (at which
 *    point the OS-level Firebase SDK has persisted it), so the caller can
 *    await it before moving on. Bounded by `timeoutMs` and never rejects — a
 *    hung bridge must not block a signup.
 */
export const trackCriticalEvent = (
  name: string,
  params?: EventParams,
  timeoutMs = 1000,
): Promise<void> => {
  if (!initPromise) return Promise.resolve(); // initAnalytics was never called
  const delivered = new Promise<void>((resolve) => {
    whenReady(() => {
      sendEvent(name, params).then(resolve, () => resolve());
    });
  });
  void flushAnalytics();
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
  return Promise.race([delivered, timeout]);
};

/**
 * Track a page view to GA4 and PostHog. Fullstory captures page views
 * automatically; PostHog's automatic pageview is off (SPA), so this sends
 * `$pageview` with the same `page_title` GA4 gets.
 */
export const trackPageView = (pageName: string): void => {
  const doTrack = () => {
    if (analytics) {
      logEvent(analytics, 'page_view', { page_title: pageName });
    }
    if (capAnalytics) {
      capAnalytics.setCurrentScreen({ screenName: pageName }).catch(() => {});
    }
    if (phApi) {
      phApi.capture('$pageview', {
        page_title: pageName,
        // origin + pathname only: query/fragment can carry auth codes.
        ...(typeof window !== 'undefined' && window.location
          ? { $current_url: `${window.location.origin}${window.location.pathname}` }
          : {}),
      });
    }
  };
  whenReady(doTrack);
};
